import {isValid as isValidDate} from 'date-fns/isValid';

import {
  ETimePeriod,
  ETimePickerType,
  MAX_TIME,
  MILLISECONDS,
  MIN_TIME,
  TTime,
  TTimePickerType,
} from './types';

const formatInputValue = (type: TTimePickerType, value: string = '') => {
  const cleanValue = value.replaceAll(/\D+/g, '');

  if (type === ETimePickerType.HMS && cleanValue.length > 4) {
    return `${cleanValue.substring(0, 2)}:${cleanValue.substring(2, 4)}:${cleanValue.substring(4, 6)}`;
  }

  if (cleanValue.length > 2) {
    return `${cleanValue.substring(0, 2)}:${cleanValue.substring(2, 4)}`;
  }

  return cleanValue;
};

const formatTimeParts = (parts: (string | number)[]) =>
  parts.map((time) => String(time).padStart(2, '0')).join(':');

const formatTime = (selectedTime: TTime, type: TTimePickerType, placeholder: string) => {
  const timeParts = placeholder.split(':');

  if (type === ETimePickerType.HM) {
    return formatTimeParts([
      selectedTime.HOURS ?? timeParts[0],
      selectedTime.MINUTES ?? timeParts[1],
    ]);
  }

  if (type === ETimePickerType.MS) {
    return formatTimeParts([
      selectedTime.MINUTES ?? timeParts[0],
      selectedTime.SECONDS ?? timeParts[1],
    ]);
  }

  return formatTimeParts([
    selectedTime.HOURS ?? timeParts[0],
    selectedTime.MINUTES ?? timeParts[1],
    selectedTime.SECONDS ?? timeParts[2],
  ]);
};

const isFilledTime = (selectedTime: TTime, type: TTimePickerType) => {
  if (type === ETimePickerType.HM) {
    return selectedTime.HOURS !== undefined && selectedTime.MINUTES !== undefined;
  }

  if (type === ETimePickerType.MS) {
    return selectedTime.MINUTES !== undefined && selectedTime.SECONDS !== undefined;
  }

  return (
    selectedTime.HOURS !== undefined &&
    selectedTime.MINUTES !== undefined &&
    selectedTime.SECONDS !== undefined
  );
};

const isAvailableTime = (
  time: TTime,
  {
    minDate,
    maxDate,
    period,
    type,
  }: {minDate: Date; maxDate: Date; period?: ETimePeriod; type: TTimePickerType},
) => {
  if (period === ETimePeriod.MINUTES && !time.HOURS && type !== ETimePickerType.MS) {
    return true;
  }

  if (period === ETimePeriod.SECONDS && (!time.MINUTES || !time.HOURS)) {
    if (type === ETimePickerType.MS && !time.MINUTES) {
      return true;
    }

    if (
      [ETimePickerType.HM, ETimePickerType.HMS].includes(type as ETimePickerType) &&
      (!time.MINUTES || !time.HOURS)
    ) {
      return true;
    }
  }

  if (period === ETimePeriod.HOURS && time.HOURS !== undefined && !time.MINUTES && !time.SECONDS) {
    return time.HOURS <= maxDate.getHours() && time.HOURS >= minDate.getHours();
  }

  const date = new Date();

  date.setHours(
    time.HOURS ?? minDate.getHours(),
    time.MINUTES ?? minDate.getMinutes(),
    time.SECONDS ?? minDate.getSeconds(),
    MILLISECONDS,
  );

  return date <= maxDate && date >= minDate;
};

const validateTime = (value: number, period: ETimePeriod) =>
  Math.min(Math.max(value, MIN_TIME), MAX_TIME[period]);

const getTimeValue = (type: TTimePickerType, value?: Date | string | null): TTime => {
  if (!value) {
    return {};
  }

  if (value instanceof Date) {
    if (!isValidDate(value)) {
      return {};
    }

    const date = new Date(value);

    return {
      HOURS: date.getHours(),
      MINUTES: date.getMinutes(),
      SECONDS: date.getSeconds(),
    };
  }

  const timeParts = value.split(':').map(Number);

  if (type === ETimePickerType.HM) {
    return {
      HOURS: validateTime(timeParts[0], ETimePeriod.HOURS),
      MINUTES: validateTime(timeParts[1], ETimePeriod.MINUTES),
    };
  }

  if (type === ETimePickerType.MS) {
    return {
      MINUTES: validateTime(timeParts[0], ETimePeriod.MINUTES),
      SECONDS: validateTime(timeParts[1], ETimePeriod.SECONDS),
    };
  }

  return {
    HOURS: validateTime(timeParts[0], ETimePeriod.HOURS),
    MINUTES: validateTime(timeParts[1], ETimePeriod.MINUTES),
    SECONDS: validateTime(timeParts[2], ETimePeriod.SECONDS),
  };
};

export {formatTime, formatInputValue, isFilledTime, getTimeValue, isAvailableTime};
